import classNames from 'classnames';

import lang from '../../../../lib/language';
import { formatCompactMetricValue, getLimitedText } from '../../../../lib/util/DataHelpers';
import { isEmpty } from '../../../../lib/util/typeGuards';

import CompanyLogo from '../../../../components/CompanyLogo/CompanyLogo';
import ClickableDiv from '../../../ClickableDiv/ClickableDiv';
import useIsMobile from '../../../hooks/useIsMobile';
import NoDataAvailable from '../../../NoDataAvailable/NoDataAvailable';
import {
  MARKET_CAP_RADAR_ROW_COUNT,
  MARKET_CAP_RADAR_SYMBOL_NAME_LIMIT,
  MARKET_CAP_RADAR_SYMBOL_NAME_LIMIT_MOBILE,
  MARKET_CAP_RADAR_TABLE_HEADERS,
} from '../../MarketCapRadar.constants';
import { getMarketCapRowKey } from '../../marketCapRadar.helpers';
import { MarketCapRadarListProps, MarketCapRow } from '../../MarketCapRadar.types';
import MarketCapPriceAndChange from '../MarketCapPriceAndChange/MarketCapPriceAndChange';

import MarketCapRadarLoaderRow from './MarketCapRadarLoaderRow';

import styles from '../../MarketCapRadar.module.scss';

const LOADER_ROW_COUNT = 4;

const MarketCapRadarList = ({
  data,
  title,
  isReady,
  isPending,
  onSymbolClick,
  footer,
}: MarketCapRadarListProps) => {
  const { isMobile } = useIsMobile();
  const hasRows = !isEmpty(data);
  const hasNoAnyData = !hasRows && !isPending;
  const nameLimit = isMobile ? MARKET_CAP_RADAR_SYMBOL_NAME_LIMIT_MOBILE : MARKET_CAP_RADAR_SYMBOL_NAME_LIMIT;

  const renderList = () => {
    if (hasNoAnyData) {
      return (
        <NoDataAvailable
          className={styles.noDataToDisplayInside}
          isNoDataIcon
          title={lang.commonDiscover52WeekNoDataAll()}
        />
      );
    }

    const results = data.slice(0, MARKET_CAP_RADAR_ROW_COUNT).map((item: MarketCapRow) => {
      const displayName = getLimitedText(item.displayName, nameLimit);

      return (
        <ClickableDiv
          className={styles.tableRow}
          key={getMarketCapRowKey(item)}
          onClick={() => onSymbolClick(item.symbol)}
        >
          <div className={styles.wrapperCompany}>
            {(item.logoUrl || item.logo) && isReady ? (
              <CompanyLogo url={item.logoUrl} logo={item.logo} symbol={item.symbol} />
            ) : (
              <div className={styles.loadingLogo} />
            )}
            <div className={styles.companyInfo}>
              <p className={GlobalClassesEnum.CommonBody1TextBold}>{item.symbol}</p>
              <p className={styles.symbolDisplayName}>{displayName}</p>
            </div>
          </div>
          <div className={styles.marketCapCell}>{formatCompactMetricValue(item.marketCap, { withCurrency: true })}</div>
          <div className={styles.priceChange}>
            <MarketCapPriceAndChange item={item} isLoading={isPending} />
          </div>
        </ClickableDiv>
      );
    });

    while (results.length < MARKET_CAP_RADAR_ROW_COUNT) {
      results.push(<div className={styles.emptyCompanyField} key={`empty-${results.length}`} />);
    }

    return results;
  };

  const tableHeaders = (
    <div className={classNames(styles.headerRow, { [styles.invisibleRow]: hasNoAnyData })}>
      {MARKET_CAP_RADAR_TABLE_HEADERS.map((header: string) => <div key={header}>{header}</div>)}
    </div>
  );

  const tableBody = isPending && !hasRows
    ? Array.from({ length: LOADER_ROW_COUNT }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <MarketCapRadarLoaderRow key={`market-cap-loader-${index}`} />
    ))
    : renderList();

  if (isMobile) {
    return (
      <div className={classNames(styles.tableMobile, { [styles.tableWithoutContainer]: hasNoAnyData })}>
        {tableHeaders}
        <div className={styles.tableBody}>{tableBody}</div>
        {footer}
      </div>
    );
  }

  return (
    <div className={classNames(styles.table, { [styles.tableWithoutContainer]: hasNoAnyData })}>
      <div className={styles.theadWrapper}>
        <div className={styles.tableTitle}>{title}</div>
        {tableHeaders}
      </div>
      <div className={styles.tableBody}>{tableBody}</div>
    </div>
  );
};

export default MarketCapRadarList;
